import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Stack} from '@mui/material';
import MessageList from './MessageList';

const BOTTOM_EPSILON = 64;

const MessagesContainer = ({
                               messages = [],
                               newUserMessageTrigger,
                           }) => {
    const scrollRef = useRef(null);
    const lastMessageRef = useRef(null);
    const [isAtBottom, setIsAtBottom] = useState(true);
    const isInitialMount = useRef(true);

    const scrollToBottom = useCallback((useSmooth = false) => {
        const container = scrollRef.current;
        if (!container) return;
        if (useSmooth) {
            container.scrollTo({
                top: container.scrollHeight,
                behavior: 'smooth',
            });
        } else {
            // Direct assignment for instant scroll (streaming, resize)
            container.scrollTop = container.scrollHeight;
        }
    }, []);

    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        // Consider within BOTTOM_EPSILON of bottom as "at bottom"
        const atBottom = Math.abs(el.scrollHeight - el.scrollTop - el.clientHeight) < BOTTOM_EPSILON;
        setIsAtBottom(atBottom);
    }, []);

    // Initial load: scroll to bottom immediately
    useEffect(() => {
        if (isInitialMount.current && messages.length > 0) {
            isInitialMount.current = false;
            scrollToBottom(false);
        }
    }, [messages.length, scrollToBottom]);

    // When messages array changes: scroll if at bottom
    useEffect(() => {
        if (isInitialMount.current) return;
        const el = scrollRef.current;
        if (isAtBottom && el) {
            el.scrollTop = el.scrollHeight;
        }
    }, [messages, isAtBottom]);

    // When user sends a message: always scroll to bottom
    useEffect(() => {
        if (!newUserMessageTrigger) return;
        scrollToBottom(true);
    }, [newUserMessageTrigger, scrollToBottom]);

    // Watch the last message for height changes (streaming content, images loading)
    useEffect(() => {
        const container = scrollRef.current;
        const last = lastMessageRef.current;
        if (!container || !last || typeof ResizeObserver === 'undefined') return;

        const ro = new ResizeObserver(() => {
            if (isAtBottom) {
                // Keep stuck to bottom while streaming or at bottom
                container.scrollTop = container.scrollHeight;
            }
        });

        ro.observe(last);
        return () => ro.disconnect();
    }, [messages.length, isAtBottom]);

    return (
        <Stack
            ref={scrollRef}
            onScroll={handleScroll}
            p={2}
            sx={{
                overflowY: 'auto',
                height: '100%',
                minHeight: 0,
            }}
        >
            <MessageList messages={messages} lastMessageRef={lastMessageRef}/>
        </Stack>
    );
};

export default MessagesContainer;

